import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Grid } from '@mui/material'
import { useReactToPrint } from 'react-to-print'

// App Components
import { DataGridColumns } from 'src/admin/core/components/DataGrid/CustomDataGrid'
import { AdminDataGrid } from 'src/admin/core/components/DataGrid/AdminDataGrid'
import { Page } from 'src/admin/core/components'
import { generateFilterGraphql } from 'src/admin/core'
import { ContentDataGrid } from 'src/admin/core/components/DataGrid'
import { PrintButton } from 'src/admin/core/components'
import { DownloadButton } from 'src/admin/core/components/Buttons/DownloadButton'
import { formatAsCurrency } from 'src/admin/core/services/formatting'
import { GridColDef } from '@mui/x-data-grid'

// Hooks

import { useAppTranslation, usePagination, useUserClaimActions } from 'src/admin/core/hooks'
import { Filters, FilterModel, initialFilters } from './components/Filters'
import { ClaimActions } from 'src/admin/core/models'
import { Products } from 'src/core/models'
import { AgencyWeeklySales } from '../weeklyBalance/models/AgencyWeeklySales'
 
import { PrintReport, downloadReportAsPDF } from './components/PrintReport'
import { useWorkstationBalances } from './hooks/useWorkstationBalances'
 
 

const pageStyle = `
  @page {
    size: 80mm 297mm; 
    margin: 0mm;  
  }
`

export type ConsolidatedData = {
  id: number
  workstationId: string
  workstationName: string
  agencyId: string
  agencyName: string
  agencyWithCode: string
  enterpriseId: string
  enterpriseName: string
  sales: number
  cancelled: number
  payments: number
  surcharge: number
  deposits: number
  withdrawals: number
  draw: number
  payDraw: number
  amount: number
  commission: number
}

const WorkstationBalanceFooter: React.FC<{ columns: GridColDef[]; data: any[] }> = ({ columns, data }) => {
  // Calculate totals for each numeric column
  const totals = {
    sales: data.reduce((sum, row) => sum + (row.sales || 0), 0),
    cancelled: data.reduce((sum, row) => sum + (row.cancelled || 0), 0),
    payments: data.reduce((sum, row) => sum + (row.payments || 0), 0),
    amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
    commission: data.reduce((sum, row) => sum + (row.commission || 0), 0),
  }

  return (
    <div style={{ display: 'flex', width: '100%' }}>
      {columns.map((col) => (
        <div
          key={col.field}
          style={{
            flex: col.flex ?? undefined,
            width: col.width ?? undefined,
            minWidth: col.minWidth ?? undefined,
            maxWidth: col.maxWidth ?? undefined,
            textAlign: col.align ?? 'left',
            padding: 8,
            fontWeight: 'bold',
          }}
        >
          {['sales', 'cancelled', 'payments', 'amount', 'commission'].includes(col.field)
            ? formatAsCurrency(totals[col.field as keyof typeof totals])
            : col.field === 'workstationName'
              ? 'Totales:'
              : ''}
        </div>
      ))}
    </div>
  )
}

const WorkstationCommissions = () => {
  const { label } = useAppTranslation()
  const [currentFilters, setCurrentFilters] = useState<FilterModel>(initialFilters)
  const { page, pageSize } = usePagination()
  const { validateClaimActions } = useUserClaimActions()
  
  const actions = validateClaimActions('consults.daily-balances', [ClaimActions.CanView, ClaimActions.CanPrint])
  const { results, loading, getWorkstationBalances } = useWorkstationBalances()
  const [consolidatedData, setConsolidatedData] = useState<ConsolidatedData[]>([])
  const [startPrinting, setStartPrinting] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  
  // Define a type for the PrintReport component reference
  type PrintReportRef = {
    getTableRef: () => React.RefObject<HTMLDivElement>
    downloadReport: () => void
  }

  // Use useRef with the correct type
  const componentRef = useRef<HTMLDivElement>(null)
  const printReportRef = useRef<PrintReportRef>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Memoize getCustomFilters to prevent recreation on every render
  const getCustomFilters = useCallback((filters: FilterModel) => {
    setFromDate(filters.startDate)
    setToDate(filters.endDate)
 
    return {
      product: filters.product,
      currency: filters.currencyId,
      from: `${filters.startDate}`,
      to: `${filters.endDate}`,
      provider: filters.provider,
      enterpriseId: filters.enterprise?.id || '',
      // Use locationId directly as agencyId
      where: filters.locationId ? {
        key: {
          id: { ...generateFilterGraphql(filters.locationId, 'eq') },
        },
      } : [],
    }
  }, [])

  // Handle filter changes from child component
  const handleFilterChange = useCallback((filters: FilterModel) => {
    setCurrentFilters(filters)
  }, [])

  // Handle search button click
  const handleSearch = useCallback((filters: FilterModel) => {
    const customFilters = getCustomFilters(filters)
    if (customFilters) {
      getWorkstationBalances(customFilters)
    }
  }, [getCustomFilters, getWorkstationBalances])

  // Remove automatic API call - now only triggered by search button

  useEffect(() => {
    const data = results.results

    if (data.length > 0) {
      const workstationGroup = groupByWorkstationId(data)

      const consolidated = []
      Object.keys(workstationGroup).forEach((workstationKey, index) => {
        const workstationData = workstationGroup[workstationKey]

        try {
          const calculatedData = calculateAmounts(workstationData)
          // const calculatedDraws = calculateDraws(workstationData)
          const workstationObj = workstationData[0]

          const products = workstationObj.key.agency.products as Products[]
          const workstationName = workstationObj.key.workstation?.name || ''
          const agencyId = workstationObj.key.workstation?.agency?.id || ''
          const agencyName = workstationObj.key.workstation?.agency?.name || ''
          const enterpriseId = workstationObj.key.workstation?.enterprise?.id || ''
          const enterpriseName = workstationObj.key.workstation?.enterprise?.name || ''
          
          consolidated.push({
            id: index,
            workstationId: workstationKey,
            workstationName: workstationName,
            agencyId: agencyId,
            agencyName: agencyName,
            agencyWithCode: `${products.length > 0 ? products[0].code : ''} ${agencyName}`,
            enterpriseId: enterpriseId,
            enterpriseName: enterpriseName,
            sales: calculatedData.sales,
            cancelled: calculatedData.cancelled,
            payments: calculatedData.payments,
            commission: calculatedData.totalCommission,
            amount: calculatedData.totalAmount,
            // ...calculatedDraws,
          })
        } catch (e) {
          setConsolidatedData([])
        }
      })

      // Sort consolidated data by workstationName from A to Z
      const sortedConsolidated = [...consolidated].sort((a, b) => a.workstationName.localeCompare(b.workstationName))

      // Filter locally by workstation field (case-insensitive)
      const workstationSearch = (currentFilters.workstation || '').trim().toLowerCase()
      
      if (workstationSearch.length > 0) {
        const filtered = sortedConsolidated.filter((d) => {
          const matchesWorkstation = d.workstationName.toLowerCase().includes(workstationSearch)
          return matchesWorkstation
        })
        setConsolidatedData(filtered)
      } else {
        setConsolidatedData(sortedConsolidated)
      }
    } else {
      setConsolidatedData([])
    }
  }, [results.results, currentFilters.workstation])

  const getColumnDefinitions = useCallback((): DataGridColumns => {
    return {
      AGENCIAS: { field: 'agencyWithCode', contentType: 'string' },
      ESTACION: { field: 'workstationName', contentType: 'string' },
      VENTAS: { field: 'sales', contentType: 'currency', headerAlign: 'right', align: 'right' },
      CANCELACIONES: { field: 'cancelled', contentType: 'currency', headerAlign: 'right', align: 'right' },
      PAGOS: { field: 'payments', contentType: 'currency', headerAlign: 'right', align: 'right' },
      BALANCE: { field: 'amount', contentType: 'currency', headerAlign: 'right', align: 'right' },
      COMISION: { field: 'commission', contentType: 'currency', headerAlign: 'right', align: 'right' },
    }
  }, [])

  const handlePrint = useCallback(() => {
    setIsProcessing(true)
    setStartPrinting(true)
    setIsProcessing(true)
    setTimeout(() => {
      printReport()
    }, 500)
  }, [])

  const handleDownload = useCallback(() => {
    setIsProcessing(true)
    setFromDate(currentFilters.startDate)
    setToDate(currentFilters.endDate)
    setStartPrinting(true)

    // Wait for the component to be rendered
    setTimeout(() => {
      try {
        if (printReportRef.current) {
          // Use the new downloadReport method directly
          printReportRef.current.downloadReport()
          setIsProcessing(false)
          setStartPrinting(false)
          return
        } else {
          // Alternative method: search for elements directly in the DOM
          const tableRefElement = componentRef.current?.querySelector('[data-ref="tableRef"]')

          if (tableRefElement) {
            // Create temporary refs to pass to the function
            const tableRef = { current: tableRefElement as HTMLElement }
            // Give a little more time to ensure elements are fully rendered
            setTimeout(() => {
              downloadReportAsPDF(
                tableRef,
                null,
                `comisiones-consorcio-${currentFilters.startDate}`,
                currentFilters.startDate,
                currentFilters.endDate,
                label(currentFilters.provider),
                currentFilters.enterprise?.name,
                undefined,
                label(currentFilters.product),
              )
              setIsProcessing(false)
              setStartPrinting(false) // Remove the temporary component after download
            }, 300)
            return
          } else {
            // Handle error: Could not find table element for PDF download
            alert('Error generating PDF: Could not find necessary elements.')
            setIsProcessing(false)
            setStartPrinting(false) // Ensure we remove the component even on error
          }
        }
      } catch (error) {
        // Handle error: Error trying to generate PDF
        alert('Error generating PDF. Please try again.')
        setIsProcessing(false)
        setStartPrinting(false) // Ensure we remove the component even on error
      }
    }, 1000) // We reduce the initial time but add additional times at critical steps
  }, [currentFilters])

  const printReport = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: pageStyle,
    onAfterPrint: () => {
      window.focus()
      setStartPrinting(false)
      setIsProcessing(false)
    },
    onBeforePrint: () => {
      setStartPrinting(false)
    },
  })

  return (
    <Page
      hasAccess={actions.canView}
      tabTitle={label('COMISIONES_POR_ESTACION_TITULO')}
      pageTitle={label('COMISIONES_POR_ESTACION_TITULO')}
      actions={
        actions.canPrint ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <DownloadButton onClick={handleDownload} disabled={consolidatedData.length === 0 || isProcessing} />
            <PrintButton onClick={handlePrint} disabled={consolidatedData.length === 0 || isProcessing} />
          </Box>
        ) : null
      }
    >
      <Grid item xs={12}>
        <Filters 
          onChange={handleFilterChange} 
          onSearch={handleSearch}
          isLoading={loading}
        />
      </Grid>
      <Grid item xs={12}>
        <ContentDataGrid>
          <AdminDataGrid
            columns={getColumnDefinitions()}
            data={consolidatedData}
            loading={loading}
            hideFooterPagination={false}
            footerComponent={WorkstationBalanceFooter}
          />
        </ContentDataGrid>
        {startPrinting && (
          <Grid ref={componentRef}>
            <PrintReport
              ref={printReportRef}
              data={consolidatedData}
              fromDate={fromDate}
              toDate={toDate}
              provider={label(currentFilters.provider)}
              enterpriseName={currentFilters.enterprise?.name}
              productName={label(currentFilters.product)}
            />
          </Grid>
        )}
      </Grid>
    </Page>
  )
}

const groupByWorkstationId = (data: any) => {
  const groupedByWorkstationId = data.reduce((result, item) => {
    const workstationName = item.key.workstation?.name
    const agencyId = item.key.workstation?.agency?.id
    // Create a unique key combining workstation name and agency ID to handle cases where
    // multiple workstations might have the same name in different agencies
    const workstationKey = `${workstationName}_${agencyId}`
    
    if (workstationName && agencyId) {
      if (!result[workstationKey]) {
        result[workstationKey] = []
      }
      result[workstationKey].push(item)
    }
    return result
  }, {})

  return groupedByWorkstationId
}

const calculateAmounts = (data: AgencyWeeklySales[]) => {
  let sales = 0
  let cancelled = 0
  let payments = 0
  let totalCommission = 0

  let totalAmount = 0

  // totalCommission = sales - commission cancellation
  data.forEach((item) => {
    const {
      amount,
      commission,
      key: { transactionType },
    } = item
    if (transactionType === 'SALE') {
      sales += amount + commission
      totalCommission += commission
    } else if (transactionType === 'CANCELLATION') {
      cancelled += amount + commission
      totalCommission -= commission
    } else if (transactionType === 'PAYMENT') {
      payments += amount
    }
  })

  totalAmount = sales - cancelled - payments
  return {
    sales,
    cancelled,
    payments,
    totalAmount,
    totalCommission,
  }
}

export default WorkstationCommissions
